import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { colorTheme } from 'theme';
import { commonConstantTexts } from 'constants/constantText';
import { strings } from 'i18n/Strings';
import { CustomDataGrid } from 'components';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { dateFormat } from 'utils/GeneralUtil';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import { getManualReconcileBankFileRecords } from 'service/UploadService';

export interface BankFileRecordType {
  transactionDate: string;
  transactionReference: string;
  transactionNarrative: string;
  creditorName: string;
  amount: string;
  status: string;
  previousTransactionNarrative: string;
}

interface BankFileGridPropsType {
  selectedRecord: BankFileRecordType;
}

type GridRowData = Record<string, unknown>;

const useStyles = makeStyles({
  emptyTransactionContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '70vh',
    width: '100%',
  },
});

const InfoModalGrid = (props: BankFileGridPropsType): JSX.Element => {
  const classes = useStyles();
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const { selectedRecord } = props;
  const [bankFileRecords, setBankFileRecords] = useState<
    BankFileRecordType[] | unknown
  >([]);
  const [reconciledBankFile, setReconciledBankFile] = useState<
    BankFileRecordType[] | unknown
  >([]);
  const [loader, setLoader] = useState(false);
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  const title = `${commonConstantTexts.markedAsManual} ${bankFileRecords?.previousTransactionNarrative} to ${bankFileRecords?.transactionNarrative} `;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getBankFileRecords = async (id: string) => {
    setLoader(true);
    await getManualReconcileBankFileRecords(id)
      .then((result) => {
        setBankFileRecords(result?.record);
        setReconciledBankFile(result?.manualReconciledRecordForRecord);
        setLoader(false);
      })
      .catch((err) => {
        setLoader(false);
        console.log(err);
      });
  };

  useEffect(() => {
    if (selectedRecord.id) {
      void getBankFileRecords(selectedRecord.id);
    }
  }, [selectedRecord.id]);

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'transactionDate',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.transactionDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row?.date)}
        </Typography>
      ),
    },
    {
      field: 'transactionReference',
      width: 200,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.transactionReference}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.transactionReference || '-'}
        </Typography>
      ),
    },
    {
      field: 'transactionNarrative',
      width: 175,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.transactionNarrative}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.transactionNarrative || '-'}
        </Typography>
      ),
    },
    {
      field: 'bankFileName',
      width: 250,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.bankFileName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.contributionBankFile?.fileName || '-'}
        </Typography>
      ),
    },
    {
      field: 'uploadDate',
      width: 150,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.uploadDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {(row?.createdAt && dateFormat(row?.createdAt)) || '-'}
        </Typography>
      ),
    },
    {
      field: 'creditOrDebit',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.creditOrDebit}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.fundFlowType || ''}
        </Typography>
      ),
    },
    {
      field: 'amount',
      width: 150,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.amountLabel}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormater(Number(Math.abs(row?.amount)).toFixed(2)) || '-'}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      {loader ? (
        <Box className={classes.emptyTransactionContainer}>
          <CircularProgress />
        </Box>
      ) : (
        <Box padding={1}>
          {selectedRecord.statusType === commonConstantTexts.manual && (
            <Typography
              component="h5"
              variant="h5"
              color={'#000000'}
              fontWeight={700}
              fontSize={18}
              fontFamily="Inter !important"
            >
              {title}
            </Typography>
          )}
          {selectedRecord?.reconciledBankRecordFileIds.length > 1 && (
            <>
              <Box paddingTop={3}>
                <Typography color={'#000000'} fontSize={16}>
                  {commonConstantTexts.reconciledByFollwing}
                </Typography>
              </Box>
              <CustomDataGrid
                columns={columns}
                rows={reconciledBankFile as GridRowData[]}
                getRowId={(row: GridRowData) => `${String(row?.id)}`}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0
                    ? commonConstantTexts.evenText
                    : commonConstantTexts.oddText
                }
                currentPage="uploadDetails"
                tableHeight={'50vh'}
                hideFooter={true}
              />
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default InfoModalGrid;
